import { Stack, Typography } from '@mui/material'
import DOMPurify from 'dompurify'
import type { FC, RefCallback } from 'react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { clampCss, optimizelyCss, unit } from '@hcr/ui'
import { isNull } from '@hcr/utils'

interface ExpandableTextProps {
  component?: React.ElementType
  lineClamp?: number
  value: string
}

export const ExpandableText: FC<ExpandableTextProps> = ({ lineClamp = 4, value, component = 'h2' }) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const [hasEllipsis, setHasEllipsis] = useState(false)

  const contentRef = useCallback<RefCallback<HTMLSpanElement>>(element => {
    if (isNull(element)) {
      return
    }

    setHasEllipsis(element.offsetHeight < element.scrollHeight)
  }, [])

  return (
    <Stack direction='row' flexWrap='wrap'>
      <Typography
        component={component}
        variant='bodyM'
        lineHeight={unit(6)}
        sx={{ wordBreak: 'break-word' }}
        css={[optimizelyCss, !isExpanded && clampCss(lineClamp)]}
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }}
        ref={contentRef}
      />
      {hasEllipsis && (
        <Typography
          component='a'
          variant='textLinkM'
          role='link'
          tabIndex={0}
          onClick={() => setIsExpanded(!isExpanded)}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              setIsExpanded(!isExpanded)
            }
          }}
        >
          {isExpanded ? t('expandable-text.read-less') : t('expandable-text.read-more')}
        </Typography>
      )}
    </Stack>
  )
}
